import React, { useState, useEffect } from 'react';
import { useRenderCount, useGlobalContext, currentTripStateIdVar, useCurrentRooms, } from '@honeycomb/data';
import { router } from '@honeycomb/routing';
import { Button2, Clone, Stacker } from '@honeycomb/ui-core';
import { useStorageState } from 'react-storage-hooks';
import { getQueryString } from '../../utils/getQueryString';
import { HotelCard } from './HotelCard/HotelCard';
import { HotelSearchNoResult, FilterResultsEnum } from './HotelSearchNoResult/HotelSearchNoResult';
import { FlightDetailsDialog } from '../flight/FlightDetails/FlightDetailsDialog';
import { validateFlightDetails } from '../flight/utils/utils';
import { HotelReviewsDialog } from '../hotel/HotelReviewsDialog/HotelReviewsDialog';
import { getStorageObject } from '../../utils/getStorageObject';
export function HotelSearchResults({ state: { hotelSearchResults, hotelSearch }, actions: { fetchNextHotels, setHotelFilters, showFullSearch }, hotelSearchResultsSlot = () => null, }) {
    var _a, _b, _c, _d, _e, _f;
    // State for flight details dialog
    const [flightDetailsDialogOpen, setFlightDetailsDialogOpen] = useState(false);
    const [flightDetails, setFlightDetails] = useState();
    // State for reviews dialog
    const [reviewsDialogOpen, setReviewsDialogOpen] = React.useState(false);
    const [reviewDetails, setReviewDetails] = useState();
    const { resourceStrings: { HOTELS_LOADMORE }, } = useGlobalContext();
    const storageObject = getStorageObject();
    const [connectingFlight, _setConnectingFlight] = useStorageState(storageObject, 'connectingFlight');
    if (connectingFlight && (connectingFlight === null || connectingFlight === void 0 ? void 0 : connectingFlight.tripId) !== currentTripStateIdVar()) {
        storageObject.removeItem('connectingFlight'); // Should this not be a useEffect? Calling this during the render looks wrong.
    }
    const [_, setCurrentRooms] = useCurrentRooms();
    const { loading: hotelsLoading, called: hotelsCalled, error, data } = hotelSearchResults;
    const loading = hotelsLoading || !hotelsCalled;
    const hotels = (_a = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _a === void 0 ? void 0 : _a.hotels;
    const totalHotelCount = (_c = (_b = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _b === void 0 ? void 0 : _b.hotelCount) !== null && _c !== void 0 ? _c : 0; // The total number of hotels in the result set.
    const filteredHotelCount = (_e = (_d = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _d === void 0 ? void 0 : _d.filteredHotelCount) !== null && _e !== void 0 ? _e : 0; // The number of hotels that match the current filter settings.
    const visibleHotelCount = (_f = hotels === null || hotels === void 0 ? void 0 : hotels.length) !== null && _f !== void 0 ? _f : 0; // The number of hotels that we are currently displaying.
    const query = router === null || router === void 0 ? void 0 : router.query();
    const queryString = getQueryString(query);
    // !! DEV-Only !! Counts and prints number of renders for component
    useRenderCount('hotelsList');
    useEffect(() => {
        setCurrentRooms({
            rooms: [],
            selectedRoomIndex: 0,
            showPerPersonPrices: false,
            soldOut: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleReviewsDialogTriggerClick = React.useCallback((hotel) => (origin) => {
        setReviewDetails({
            hotel,
            origin,
        });
        setReviewsDialogOpen(true);
    }, []);
    if (error) {
        return React.createElement("div", null,
            "Error!: ",
            error.message);
    }
    const filtersApplied = filteredHotelCount < totalHotelCount
        ? FilterResultsEnum.FilterResultAvailable
        : FilterResultsEnum.NoFilterResultAvailable;
    if (!loading && data && !(hotels === null || hotels === void 0 ? void 0 : hotels.length)) {
        return (React.createElement(HotelSearchNoResult, { state: { filterResultReason: filtersApplied, hotelCount: filteredHotelCount, hotelSearch }, actions: { setHotelFilters, showFullSearch } }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Stacker, { spacing: { xs: 5, xl: 6 }, alignItems: "center" }, hotels === null || hotels === void 0 ? void 0 :
            hotels.map((searchResult, index, arr) => {
                return (React.createElement(React.Fragment, { key: `hotel-result-${searchResult.hotel.estabId.toString()}}` },
                    React.createElement(hotelSearchResultsSlot, { index, hotels: arr }),
                    React.createElement(HotelCard, { searchResult: searchResult, queryString: queryString, onFlightDetailsClick: validateFlightDetails(searchResult.flight)
                            ? () => {
                                var _a, _b;
                                setFlightDetails({
                                    outboundLeg: (_a = searchResult.flight) === null || _a === void 0 ? void 0 : _a.outboundLeg,
                                    inboundLeg: (_b = searchResult.flight) === null || _b === void 0 ? void 0 : _b.inboundLeg,
                                });
                                setFlightDetailsDialogOpen(true);
                            }
                            : undefined, onReviewsDialogTriggerClick: handleReviewsDialogTriggerClick(searchResult.hotel), "data-id": "hotel_search_results_listing", key: `hotel-card-${searchResult.hotel.estabId.toString()}}` })));
            }),
            loading && React.createElement(Clone, { copies: 6, renderChildren: () => React.createElement(HotelCard, { "data-id": "skeleton-card" }) }),
            !loading && visibleHotelCount < filteredHotelCount && (React.createElement(Button2, { onClick: fetchNextHotels, fullWidth: true, variant: "outlined", size: "l", color: "primary", "data-id": "show_more_button" }, HOTELS_LOADMORE))),
        React.createElement(FlightDetailsDialog, { outboundLeg: (flightDetails === null || flightDetails === void 0 ? void 0 : flightDetails.outboundLeg) || undefined, inboundLeg: (flightDetails === null || flightDetails === void 0 ? void 0 : flightDetails.inboundLeg) || undefined, open: flightDetailsDialogOpen, onClose: () => {
                setFlightDetailsDialogOpen(false);
            } }),
        reviewDetails && (React.createElement(HotelReviewsDialog, { hotel: reviewDetails.hotel, origin: reviewDetails.origin, open: reviewsDialogOpen, onClose: () => {
                setReviewsDialogOpen(false);
            } }))));
}
